import { useState, useEffect, useCallback, useRef } from 'react';

// Market hours: 9:30 AM - 4:00 PM Mon-Fri local time = Light
// Outside those hours = Dark
// Auto switches only twice per day (at 9:30 AM and 4:00 PM)

function isMarketHours(date) {
  const day = date.getDay();
  if (day === 0 || day === 6) return false;
  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  return totalMinutes >= 570 && totalMinutes < 960; // 9:30 AM to 4:00 PM
}

function getSavedPreference() {
  try {
    return localStorage.getItem('theme-preference') || 'auto';
  } catch {
    return 'auto';
  }
}

function applyDark(dark) {
  if (dark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export function useMarketTheme() {
  const [preference, setPreferenceState] = useState(getSavedPreference);
  const prefRef = useRef(preference);

  // Resolve current theme
  const resolve = (pref) => {
    if (pref === 'light') return false;
    if (pref === 'dark') return true;
    return !isMarketHours(new Date());
  };

  const [isDark, setIsDark] = useState(() => resolve(getSavedPreference()));

  // Keep ref in sync
  prefRef.current = preference;

  // Apply on mount and when preference changes
  useEffect(() => {
    const dark = resolve(preference);
    setIsDark(dark);
    applyDark(dark);
  }, [preference]);

  // Auto mode: check once per minute, but only switch at the two transition points
  useEffect(() => {
    if (preference !== 'auto') return;

    let lastMarketState = isMarketHours(new Date());

    const interval = setInterval(() => {
      if (prefRef.current !== 'auto') return;
      const nowMarket = isMarketHours(new Date());
      // Only update if the market state actually changed (open->close or close->open)
      if (nowMarket !== lastMarketState) {
        lastMarketState = nowMarket;
        const dark = !nowMarket;
        setIsDark(dark);
        applyDark(dark);
      }
    }, 30000); // check every 30s

    return () => clearInterval(interval);
  }, [preference]);

  const setThemePreference = useCallback((pref) => {
    prefRef.current = pref;
    setPreferenceState(pref);
    try {
      localStorage.setItem('theme-preference', pref);
    } catch {}
    const dark = resolve(pref);
    setIsDark(dark);
    applyDark(dark);
  }, []);

  return { isDark, preference, setThemePreference };
}
